if (!customElements.get('nl-product-bundle')) {
  class NolimitProductBundle extends HTMLElement {
    connectedCallback() {
      this.status = this.querySelector('[data-bundle-status]');
      this.choices = [...this.querySelectorAll('[data-addon-choice]')];
      this.bundleSubmitButton = this.querySelector('[data-bundle-submit]');
      this.bundleSubmitText = this.querySelector('[data-bundle-submit-text]');
      this.currency = this.dataset.currency || 'USD';
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';

      if (!this.choices.length) return;

      try {
        this.mainVariants = JSON.parse(this.dataset.mainVariants || '[]');
      } catch (_error) {
        this.mainVariants = [];
      }

      this.handleChoiceChange = this.handleChoiceChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handlePublishedVariantChange = this.handlePublishedVariantChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleBundleClick = this.handleBundleClick.bind(this);
      this.handleColorClick = this.handleColorClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.addEventListener('change', this.handleChoiceChange);
      this.addEventListener('click', this.handleColorClick);
      this.bundleSubmitButton?.addEventListener('click', this.handleBundleClick);
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      this.productSection = this.closest('.shopify-section');
      this.productSection?.addEventListener('change', this.handleVariantChange);
      this.productSection?.addEventListener('submit', this.handleSubmit, true);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handlePublishedVariantChange,
        );
      }
      this.formObserver = new MutationObserver(() => this.bindProductForm());
      this.formObserver.observe(this.parentElement, { childList: true, subtree: true });
      this.bindProductForm();
      this.syncAddonColor(this.getSelectedMainVariant()?.option1);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChoiceChange);
      this.removeEventListener('click', this.handleColorClick);
      this.bundleSubmitButton?.removeEventListener('click', this.handleBundleClick);
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      this.productSection?.removeEventListener('change', this.handleVariantChange);
      this.productSection?.removeEventListener('submit', this.handleSubmit, true);
      this.variantChangeUnsubscriber?.();
      this.formObserver?.disconnect();
      window.setTimeout(() => {
        if (!document.querySelector('nl-product-bundle [data-addon-choice]:checked')) {
          document.body.classList.remove('nl-bundle-selected');
        }
      }, 0);
    }

    bindProductForm() {
      const sibling = this.nextElementSibling;
      const productForm = sibling?.matches('product-form') ? sibling : sibling?.querySelector('product-form');
      const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
      const submitButton = form?.querySelector('[name="add"]');
      if (!productForm || !form || !submitButton) return;
      if (form === this.form && submitButton === this.submitButton) return;

      this.productForm = productForm;
      this.form = form;
      this.submitButton = submitButton;
      this.submitText = submitButton.querySelector('span');
      this.defaultSubmitText ||= this.submitText?.textContent.trim() || 'Add to cart';
      this.classList.add('nl-bundle--ready');
      this.updateState();
    }

    get selectedChoices() {
      return this.choices.filter((choice) => choice.checked);
    }

    getSelectedMainVariant() {
      const variantInput = this.productForm?.querySelector('.product-variant-id');
      return this.mainVariants.find((variant) => String(variant.id) === String(variantInput?.value));
    }

    handleChoiceChange(event) {
      const choice = event.target.closest('[data-addon-choice]');

      if (choice) {
        this.updateState();
      }
    }

    handleColorClick(event) {
      const trigger = event.target.closest('.nl-bundle__color-trigger');
      if (trigger) {
        const currentMenu = trigger.closest('[data-addon-color-menu]');
        this.querySelectorAll('[data-addon-color-menu][open]').forEach((menu) => {
          if (menu !== currentMenu) menu.removeAttribute('open');
        });
        return;
      }

      const option = event.target.closest('[data-addon-variant-option]');
      if (!option || option.disabled) return;

      event.preventDefault();
      const item = option.closest('[data-addon-item]');
      const choice = item?.querySelector('[data-addon-choice]');
      if (!item || !choice) return;

      this.applyAddonOption(item, option);
      choice.checked = true;
      option.closest('[data-addon-color-menu]')?.removeAttribute('open');
      this.updateState();
    }

    handleDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.closeColorMenus();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closeColorMenus();
    }

    closeColorMenus() {
      this.querySelectorAll('[data-addon-color-menu][open]').forEach((menu) => {
        menu.removeAttribute('open');
      });
    }

    handleVariantChange(event) {
      if (event.target.matches?.('.product-variant-id')) {
        window.setTimeout(() => {
          if (!this.isConnected || this.ownerDocument !== document) return;
          this.bindProductForm();
          this.syncAddonColor(this.getSelectedMainVariant()?.option1);
          this.updateState();
        }, 0);
        return;
      }

      if (!event.target.closest('variant-selects')) return;
      const selectedColor = event.target.value;
      [0, 200, 700, 1500].forEach((delay) => {
        window.setTimeout(() => {
          if (!this.isConnected || this.ownerDocument !== document) return;
          this.bindProductForm();
          this.syncAddonColor(selectedColor);
          this.updateState();
        }, delay);
      });
    }

    handlePublishedVariantChange({ data } = {}) {
      window.setTimeout(() => {
        this.bindProductForm();
        this.syncAddonColor(data?.variant?.option1);
        this.updateState();
      }, 0);
    }

    syncAddonColor(color) {
      if (!color) return;
      this.querySelectorAll('[data-addon-item]').forEach((item) => {
        const matchingOption = [...item.querySelectorAll('[data-addon-variant-option]')].find(
          (option) => !option.disabled && option.dataset.optionName?.toLowerCase() === String(color).toLowerCase(),
        );
        if (!matchingOption) return;
        this.applyAddonOption(item, matchingOption);
      });
    }

    applyAddonOption(item, option) {
      const choice = item.querySelector('[data-addon-choice]');
      if (!choice) return;

      choice.value = option.value;
      choice.dataset.price = option.dataset.price;
      choice.dataset.discountedPrice = option.dataset.discountedPrice;
      item.querySelectorAll('[data-addon-variant-option]').forEach((candidate) => {
        candidate.setAttribute('aria-selected', String(candidate === option));
      });

      const currentOption = item.querySelector('[data-addon-current-option]');
      if (currentOption) currentOption.textContent = option.dataset.optionName || option.textContent.trim();

      const image = item.querySelector('.nl-bundle__image');
      if (image && option.dataset.imageUrl) {
        image.src = option.dataset.imageUrl;
        image.srcset = option.dataset.imageSrcset || '';
        image.alt = option.dataset.imageAlt || image.alt;
      }

      this.updateItemPrices(item, option);
    }

    updateItemPrices(item, option) {
      const regularPrice = item.querySelector('[data-addon-regular-price]');
      const salePrice = item.querySelector('[data-addon-sale-price]');
      if (regularPrice) regularPrice.textContent = this.formatMoney(option.dataset.price);
      if (salePrice) salePrice.textContent = this.formatMoney(option.dataset.discountedPrice);
    }

    updateState() {
      if (!this.isConnected || this.ownerDocument !== document || !this.productForm) return;
      const choices = this.selectedChoices;
      const hasBundle = choices.length > 0;
      this.productForm.classList.toggle('nl-bundle-active', hasBundle);
      document.body.classList.toggle('nl-bundle-selected', hasBundle);
      this.status.hidden = !hasBundle;

      if (!hasBundle) {
        if (this.submitText) this.submitText.textContent = this.defaultSubmitText;
        return;
      }

      const mainPrice = this.getSelectedMainVariant()?.price || 0;
      const addonPrice = choices.reduce(
        (total, choice) => total + Number(choice.dataset.discountedPrice || 0),
        0,
      );
      const bundlePrice = mainPrice + addonPrice;
      const buttonText = this.dataset.ctaTemplate
        .replace('[count]', String(choices.length + 1))
        .replace('[total]', this.formatMoney(bundlePrice));
      if (this.bundleSubmitText) this.bundleSubmitText.textContent = buttonText;
      if (this.submitText) this.submitText.textContent = buttonText;
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `${this.currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    handleBundleClick(event) {
      event.preventDefault();
      const form = this.productSection?.querySelector('product-form form[data-type="add-to-cart-form"]');
      form?.requestSubmit();
    }

    async handleSubmit(event) {
      const choices = this.selectedChoices;
      if (!choices.length || !event.target?.matches?.('form[data-type="add-to-cart-form"]')) return;

      this.form = event.target;
      this.productForm = this.form.closest('product-form');
      this.submitButton = this.form.querySelector('[name="add"]');
      this.submitText = this.submitButton?.querySelector('span');

      event.preventDefault();
      event.stopPropagation();

      if (this.isSubmitting) return;

      const variantInput = this.productForm.querySelector('.product-variant-id');
      const mainVariantId = Number(variantInput?.value);
      const addonVariantIds = choices.map((choice) => Number(choice.value));
      if (!mainVariantId || addonVariantIds.some((id) => !id)) return;

      this.setLoading(true);
      this.clearError();

      try {
        const formData = new FormData(this.form);
        const quantity = Math.max(1, Number(formData.get('quantity') || 1));
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              { id: mainVariantId, quantity },
              ...addonVariantIds.map((id) => ({ id, quantity: 1 })),
            ],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || this.dataset.errorMessage);
        }

        if (cart && result.sections) {
          this.renderCart(cart, result);
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'nl-product-bundle',
            productVariantId: String(mainVariantId),
            cartData: result,
          });
        }
      } catch (error) {
        this.showError(error.message || this.dataset.errorMessage);
      } finally {
        this.setLoading(false);
      }
    }

    renderCart(cart, result) {
      const productSection = result.sections['cart-notification-product'];
      const parsedProductSection = productSection
        ? new DOMParser().parseFromString(productSection, 'text/html')
        : null;
      const addedItems = (result.items || [])
        .map((item) => parsedProductSection?.getElementById(`cart-notification-product-${item.key}`))
        .filter(Boolean);
      const firstItem = addedItems[0] || parsedProductSection?.querySelector('[id^="cart-notification-product-"]');

      if (firstItem) {
        result.key = firstItem.id.replace('cart-notification-product-', '');
      } else if (result.items?.[0]?.key) {
        result.key = result.items[0].key;
      }

      cart.renderContents(result);

      if (cart.tagName === 'CART-NOTIFICATION' && parsedProductSection) {
        const target = document.getElementById('cart-notification-product');
        if (target && addedItems.length > 1) {
          const list = document.createElement('div');
          list.className = 'nl-cart-notification-items';
          addedItems.forEach((item) => list.append(item.cloneNode(true)));
          target.replaceChildren(list);
        }
      }
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      [this.submitButton, this.bundleSubmitButton].filter(Boolean).forEach((button) => {
        button.toggleAttribute('aria-disabled', isLoading);
        button.classList.toggle('loading', isLoading);
      });
      const spinner = this.submitButton?.querySelector('.loading__spinner');
      spinner?.classList.toggle('hidden', !isLoading);
    }

    clearError() {
      const wrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
      if (wrapper) wrapper.hidden = true;
    }

    showError(message) {
      const wrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
      const messageElement = wrapper?.querySelector('.product-form__error-message');
      if (!wrapper || !messageElement) return;
      messageElement.textContent = message;
      wrapper.hidden = false;
    }
  }

  customElements.define('nl-product-bundle', NolimitProductBundle);
}
